import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { translate } from "@/lib/i18n";

const Ctx = createContext(null);
export const useApp = () => useContext(Ctx);

export function AppProvider({ children }) {
  const [user, setUser] = useState(null);
  const [meta, setMeta] = useState(null);
  const [summary, setSummary] = useState(null);
  const [lang, setLangState] = useState(localStorage.getItem("ks_lang") || "en");

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    }
  }, []);

  const refreshSummary = useCallback(async () => {
    try {
      const { data } = await api.get("/summary");
      setSummary(data);
    } catch {}
  }, []);

  useEffect(() => {
    const ref = new URLSearchParams(window.location.search).get("ref");
    if (ref) localStorage.setItem("ks_ref", ref.toUpperCase());
    api.get("/meta").then((r) => setMeta(r.data)).catch(() => {});
    // Skip /me when returning from Google; AuthCallback establishes the session first.
    if (window.location.pathname === "/auth/google") return;
    if (window.location.pathname.startsWith("/admin")) return setUser(false);
    refreshUser();
  }, [refreshUser]);

  useEffect(() => {
    const l = user?.settings?.language;
    if (l) { setLangState(l); localStorage.setItem("ks_lang", l); }
  }, [user]);

  useEffect(() => {
    const ref = localStorage.getItem("ks_ref");
    if (!user?.user_id || !ref || user.referred_by) return;
    api.post("/referrals/apply", { code: ref }).then(() => localStorage.removeItem("ks_ref"))
      .catch((e) => { if (e?.response?.status === 400 && !/before your first/.test(e.response.data?.detail || "")) localStorage.removeItem("ks_ref"); });
  }, [user?.user_id]); // eslint-disable-line

  useEffect(() => {
    if (!user?.onboarded) return;
    refreshSummary();
    const id = setInterval(refreshSummary, 60000);
    return () => clearInterval(id);
  }, [user?.onboarded, refreshSummary]);

  const setLang = async (l) => {
    setLangState(l);
    localStorage.setItem("ks_lang", l);
    if (user) await api.put("/settings", { language: l }).catch(() => {});
  };

  const t = useCallback((s) => translate(lang, s), [lang]);

  return (
    <Ctx.Provider value={{ user, setUser, refreshUser, meta, summary, refreshSummary, lang, setLang, t }}>
      {children}
    </Ctx.Provider>
  );
}
